import { createElement, type ReactElement } from "react";
import { Store } from "../types/store";
import { TerritoryRegion } from "../types/territoryRegion";
import { isStoreOrAlertMode, isRegionMode } from "./modeUtils";

import { ReactComponent as DtHyperIcon } from "../media/dt-hyper.svg";
import { ReactComponent as DtDrMarketIcon } from "../media/dt-dr-market.svg";

const ICON_SIZE = { width: 20, height: 20 };

const iconMap: Record<string, typeof DtHyperIcon> = {
  "hypermarket-territory": DtHyperIcon,
  "market-region": DtDrMarketIcon,
  "market-territory": DtDrMarketIcon
};

// Cache for memoized icon elements - reuses same element objects
const iconElementCache = new Map<string, ReactElement | null>();

const getCacheKey = (
  iconKey: string,
  size: Record<string, number>
): string => {
  return `${iconKey}::${JSON.stringify(size)}`;
};

export const showIcon = (
  territory: TerritoryRegion | Store | null | undefined,
  mode: "store" | "region" | "alert",
  size = ICON_SIZE
): ReactElement | null => {
  if (!territory) {
    return null;
  }

  if (isStoreOrAlertMode(mode) && "storeType" in territory) {
    const iconKey = `${territory.storeType}-territory`;
    const Icon = iconMap[iconKey];
    if (!Icon) return null;
    
    const cacheKey = getCacheKey(iconKey, size);
    if (!iconElementCache.has(cacheKey)) {
      iconElementCache.set(cacheKey, createElement(Icon, size));
    }
    return iconElementCache.get(cacheKey) || null;
  }

  if (isRegionMode(mode) && "format" in territory && "type" in territory) {
    const iconKey = `${territory.format}-${territory.type}`;
    const Icon = iconMap[iconKey];
    if (!Icon) return null;
    
    const cacheKey = getCacheKey(iconKey, size);
    if (!iconElementCache.has(cacheKey)) {
      iconElementCache.set(cacheKey, createElement(Icon, size));
    }
    return iconElementCache.get(cacheKey) || null;
  }

  return null;
};
